import { CartCost, Money, useCart } from "@shopify/hydrogen-react";
import type {
  CartLineCost,
  Merchandise,
} from "@shopify/hydrogen-react/storefront-api-types";
import { FlexRow, Link } from "atoms";
import { pluralize } from "utils/text";

function CartDetails() {
  const {
    lines,
    linesUpdate,
    linesRemove,
    cost,
    totalQuantity = 0,
    checkoutUrl,
  } = useCart();

  const createLineIncrement = (id: string, qty: number) => () =>
    linesUpdate([{ id, quantity: qty + 1 }]);
  const createLineDecrement = (id: string, qty: number) => () =>
    linesUpdate([{ id, quantity: qty - 1 }]);
  const createLineRemove = (id: string) => () => linesRemove([id]);

  return (
    <div>
      <p>
        <Link to="/dev-shop">&larr; Collections</Link>
      </p>
      <br />

      <h2>Cart</h2>

      {totalQuantity ? (
        <div>
          <p>{pluralize(totalQuantity, "Item")}</p>

          {lines && (
            <ul>
              {lines
                .filter(isLineItem)
                .map(({ id, quantity, merchandise, cost: lineCost }) => (
                  <li key={id}>
                    <FlexRow justifyContent="space-between">
                      <span>{merchandise.title}</span>

                      <FlexRow>
                        <button
                          type="button"
                          onClick={createLineDecrement(id, quantity)}
                        >
                          -
                        </button>
                        <span>(qty: {quantity})</span>
                        <button
                          type="button"
                          onClick={createLineIncrement(id, quantity)}
                        >
                          +
                        </button>
                        <button type="button" onClick={createLineRemove(id)}>
                          x
                        </button>
                      </FlexRow>

                      <span>
                        {lineCost?.totalAmount && (
                          <Money data={lineCost?.totalAmount} />
                        )}
                      </span>
                    </FlexRow>
                  </li>
                ))}
            </ul>
          )}

          <FlexRow flexDirection="column" alignItems="flex-end">
            {cost?.subtotalAmount && (
              <div>
                Subtotal: <CartCost amountType="subtotal" as="span" />
              </div>
            )}
            {cost?.totalTaxAmount && (
              <div>
                Tax: <CartCost amountType="tax" />
              </div>
            )}
            <div>
              <strong>
                Total: <CartCost amountType="total" as="span" />
              </strong>
            </div>
          </FlexRow>
        </div>
      ) : (
        <p>
          Looks like you don&apos;t have anything in your cart. Keep shopping!
        </p>
      )}

      <br />
      <br />
      <FlexRow justifyContent="flex-end">
        <Link to="/dev-shop" variant="secondary">
          Continue Shopping
        </Link>
        {checkoutUrl && totalQuantity > 0 && (
          <Link href={checkoutUrl} variant="primary">
            Checkout
          </Link>
        )}
      </FlexRow>
    </div>
  );
}

type LineItem = {
  id: string;
  quantity: number;
  merchandise: Merchandise;
  cost: CartLineCost;
};
const isLineItem = (line: unknown): line is LineItem =>
  !!line &&
  typeof line === "object" &&
  "id" in line &&
  typeof line.id === "string" &&
  "quantity" in line &&
  typeof line.quantity === "number" &&
  "merchandise" in line &&
  typeof line.merchandise === "object" &&
  "cost" in line &&
  typeof line.cost === "object";

export { CartDetails };
